import type { Classifier } from '@zg/shared/property';

// Mirrors the pattern used by lib/api.ts for the indication flow. Both local
// dev and prod hit the same API subdomain; CORS allows localhost explicitly.
const DEFAULT_BASE_URL = 'https://api.insurance.zacharycompanies.ai';

export interface SaveDraftRequest {
  draftId?: string;
  email?: string;
  classifier?: Classifier | null;
  answers?: Record<string, unknown>;
}

export interface SaveDraftResponse {
  draftId: string;
  token: string | null;
  expiresAt: number | null;
  magicLinkSent: boolean;
}

export interface GetDraftResponse {
  draftId: string;
  classifier: Classifier | null;
  answers: Record<string, unknown>;
  updatedAt: string;
}

export class PropertyApiError extends Error {
  code: string;
  status: number;
  details?: unknown;
  constructor(code: string, status: number, message?: string, details?: unknown) {
    super(message ?? code);
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

async function handle<T>(res: Response): Promise<T> {
  const text = await res.text();
  const body = text ? safeJson(text) : null;
  if (!res.ok) {
    const code = (body as { error?: string } | null)?.error ?? `http_${res.status}`;
    throw new PropertyApiError(code, res.status, `API ${res.status}`, body);
  }
  return (body ?? {}) as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function url(path: string): string {
  return DEFAULT_BASE_URL ? `${DEFAULT_BASE_URL}${path}` : path;
}

export async function saveDraft(
  req: SaveDraftRequest
): Promise<SaveDraftResponse> {
  const res = await fetch(url('/property/draft'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(req),
  });
  return handle<SaveDraftResponse>(res);
}

export async function getDraft(token: string): Promise<GetDraftResponse> {
  const res = await fetch(url(`/property/draft/${encodeURIComponent(token)}`), {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });
  return handle<GetDraftResponse>(res);
}

export interface SubmitRequest {
  draftId: string;
  email: string;
  consent: true;
}

export interface SubmitResponse {
  submissionId: string;
  submittedAt: string;
  attachments: Array<{ template: string; filename: string; sizeKB: number }>;
  sesMessageId: string;
}

export async function submitApplication(
  req: SubmitRequest
): Promise<SubmitResponse> {
  const res = await fetch(url('/property/submit'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(req),
  });
  return handle<SubmitResponse>(res);
}
